import Spin from "@/shared/feedback/spin";
import { SignStatusHandler } from "@/modules/signing/shared/ui/sign-status-handler";
import { QrFromBase64 } from "@/modules/signing/shared/ui/qr-from-base64";
import Button from "@/shared/form/button";
import { ArrowRightOutlined } from "@ant-design/icons";
import { useTranslations } from "use-intl";
import { useExternalSignEgovLink } from "@/modules/gateway/features/open-external-sign/model/use-external-sign-egov-link";
import { useCode } from "@/modules/gateway/features/open-external-sign/model/use-code";
import { useNotification } from "@/shared/feedback/notification";
import { SignCALayerButton } from "@/modules/gateway/features/open-external-sign/ui/sign-button/sign-nca-layer";
import {
  SignRegistrationForExternalSignPageFragment,
  SignStatus,
} from "gql/graphql";
import Divider from "@/shared/layout/divider";
import { useRegistrationByCode } from "@/modules/gateway/features/open-external-sign/model/use-registration-by-code";
import { EgovSigningInstruction } from "@/modules/gateway/features/open-external-sign/view/egov-signing-instruction";
import { useCallback, useEffect } from "react";

export function Sign({
  signRegistration,
  signStatus,
}: {
  signRegistration: SignRegistrationForExternalSignPageFragment;
  signStatus: SignStatus;
}) {
  const t = useTranslations("Signing");
  const { code } = useCode();
  const {
    launchEgovMobileLink,
    launchEgovBusinessLink,
    qrCode,
    loading: linkLoading,
  } = useExternalSignEgovLink({ code });

  const notify = useNotification();
  const { refetch } = useRegistrationByCode({ code });

  const afterSign = useCallback(async () => {
    notify?.success({
      key: "signing-success",
      message: t("Success"),
      description: t("Document signed successfully"),
    });
    await refetch();
  }, [notify, refetch, t]);

  useEffect(() => {
    if (signStatus === SignStatus.Signed) {
      afterSign();
    }
  }, [afterSign, signStatus]);

  return (
    <Spin spinning={linkLoading}>
      <SignStatusHandler
        signStatus={signStatus}
        onPending={
          <>
            <div className={"only-desktop"}>
              <div className={"flex justify-center"}>
                <div className={"aspect-square w-[300px]"}>
                  <QrFromBase64 qrCode={qrCode} />
                </div>
              </div>
              <EgovSigningInstruction />
            </div>

            <div className={"only-mobile flex flex-col items-center gap-2"}>
              <Button
                type={"link"}
                href={launchEgovMobileLink}
                icon={<ArrowRightOutlined />}
                iconPosition={"end"}
              >
                {t("Sign with eGov Mobile")}
              </Button>
              <Button
                type={"link"}
                href={launchEgovBusinessLink}
                icon={<ArrowRightOutlined />}
                iconPosition={"end"}
              >
                {t("Sign with eGov Business")}
              </Button>
            </div>

            <Divider className="only-desktop">{t("Or")}</Divider>
            <div className="only-desktop">
              <SignCALayerButton
                signRegistration={signRegistration}
                registrationCode={code as string}
                afterSign={afterSign}
              />
            </div>
          </>
        }
      />
    </Spin>
  );
}
